import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MapBounds, MapStatus } from '@/types/homepage';
import { log } from '@/utils/logger';

/**
 * Map Store - Domain-focused state management for map operations
 * 
 * Responsibilities:
 * - Map state and status
 * - Map bounds management
 * - Map interactions (move, zoom, fly)
 * - Map instance reference
 * 
 * Following industry best practices:
 * - Single Responsibility Principle
 * - Immutable state updates
 * - Separation of concerns
 * - Type safety
 */

// ===== INTERFACES =====

interface MapState {
  // Map instance and reference
  mapRef: any;
  
  // Map bounds and location
  currentBounds: MapBounds | null;
  lastFetchedBounds: MapBounds | null;
  pendingBounds: MapBounds | null;
  
  // Map status and state
  mapStatus: MapStatus;
  
  // Map UI state
  isMapFullscreen: boolean;
  
  // Map interaction state
  isFlying: boolean;
  userHasInteracted: boolean;
  
  // Map loading state
  isMapLoading: boolean;
  mapError: string | null;
}

interface MapActions {
  // Map instance management
  setMapRef: (ref: any) => void;
  
  // Map bounds operations
  setCurrentBounds: (bounds: MapBounds | null) => void;
  setLastFetchedBounds: (bounds: MapBounds | null) => void;
  setPendingBounds: (bounds: MapBounds | null) => void;
  
  // Map status operations
  setMapStatus: (status: MapStatus) => void;
  updateMapStatus: (updates: Partial<MapStatus>) => void;
  
  // Map interaction operations
  setFlying: (isFlying: boolean) => void;
  setUserInteracted: (hasInteracted: boolean) => void;
  
  // Map UI operations
  setMapFullscreen: (isFullscreen: boolean) => void;
  
  // Map state management
  setMapLoading: (isLoading: boolean) => void;
  setMapError: (error: string | null) => void;
  
  // Utility functions
  resetMapState: () => void;
  isMapReady: () => boolean;
}

type MapStore = MapState & MapActions;

// ===== INITIAL STATE =====

const initialMapStatus: MapStatus = {
  state: 'initializing',
  lastLoadTime: 0,
  userHasInteracted: false,
  initialLoadCompleted: false,
  businessesStable: false,
};

const initialState: MapState = {
  mapRef: null,
  currentBounds: null,
  lastFetchedBounds: null,
  pendingBounds: null,
  mapStatus: initialMapStatus,
  isMapFullscreen: false,
  isFlying: false,
  userHasInteracted: false,
  isMapLoading: false,
  mapError: null,
};

// ===== UTILITIES =====

/**
 * Compare two MapBounds objects for equality
 */
const areBoundsEqual = (bounds1: MapBounds | null, bounds2: MapBounds | null): boolean => {
  if (!bounds1 || !bounds2) return bounds1 === bounds2;
  
  const threshold = 0.001;
  return (
    Math.abs(bounds1.northEast.latitude - bounds2.northEast.latitude) < threshold &&
    Math.abs(bounds1.northEast.longitude - bounds2.northEast.longitude) < threshold &&
    Math.abs(bounds1.southWest.latitude - bounds2.southWest.latitude) < threshold &&
    Math.abs(bounds1.southWest.longitude - bounds2.southWest.longitude) < threshold
  );
};

// ===== STORE IMPLEMENTATION =====

export const useMapStore = create<MapStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ===== MAP INSTANCE MANAGEMENT =====
      
      setMapRef: (ref: any) => {
        const currentRef = get().mapRef;
        
        // Only update if actually changing
        if (currentRef !== ref) {
          set({ mapRef: ref }, false, 'map/setMapRef');
          
          // Update map status when map instance is set
          if (ref) {
            const { updateMapStatus } = get();
            updateMapStatus({ state: 'ready' });
          }
        }
      },

      // ===== MAP BOUNDS OPERATIONS =====
      
      setCurrentBounds: (bounds: MapBounds | null) => {
        const currentBounds = get().currentBounds;
        
        // Only update if bounds actually changed
        if (!areBoundsEqual(currentBounds, bounds)) {
          set({ currentBounds: bounds }, false, 'map/setCurrentBounds');
        }
      },

      setLastFetchedBounds: (bounds: MapBounds | null) => {
        set({ lastFetchedBounds: bounds }, false, 'map/setLastFetchedBounds');
      },

      setPendingBounds: (bounds: MapBounds | null) => {
        set({ pendingBounds: bounds }, false, 'map/setPendingBounds');
      },

      // ===== MAP STATUS OPERATIONS =====
      
      setMapStatus: (status: MapStatus) => {
        set({ mapStatus: status }, false, 'map/setMapStatus');
      },

      updateMapStatus: (updates: Partial<MapStatus>) => {
        set(state => ({
          mapStatus: { ...state.mapStatus, ...updates }
        }), false, 'map/updateMapStatus');
      },

      // ===== MAP INTERACTION OPERATIONS =====
      
      setFlying: (isFlying: boolean) => {
        const currentFlying = get().isFlying;
        
        if (currentFlying !== isFlying) {
          set({ isFlying }, false, 'map/setFlying');
          
          // Update map status when flying state changes
          const { updateMapStatus } = get();
          updateMapStatus({ 
            state: isFlying ? 'flying' : 'idle' 
          });
        }
      },

      setUserInteracted: (hasInteracted: boolean) => {
        const currentInteracted = get().userHasInteracted;
        
        if (currentInteracted !== hasInteracted) {
          set({ userHasInteracted: hasInteracted }, false, 'map/setUserInteracted');
          
          // Update map status
          const { updateMapStatus } = get();
          updateMapStatus({ userHasInteracted: hasInteracted });
        }
      },

      // ===== MAP UI OPERATIONS =====
      
      setMapFullscreen: (isFullscreen: boolean) => {
        set({ isMapFullscreen: isFullscreen }, false, 'map/setMapFullscreen');
      },

      // ===== MAP STATE MANAGEMENT =====
      
      setMapLoading: (isLoading: boolean) => {
        const currentLoading = get().isMapLoading;
        
        if (currentLoading !== isLoading) {
          set({ isMapLoading: isLoading }, false, 'map/setMapLoading');
          
          // Update map status
          const { updateMapStatus } = get();
          updateMapStatus({ 
            state: isLoading ? 'loading' : 'idle' 
          });
        }
      },

      setMapError: (error: string | null) => {
        set({ mapError: error }, false, 'map/setMapError');
        
        if (error) {
          const { updateMapStatus } = get();
          updateMapStatus({ state: 'error' });
        }
      },

      // ===== UTILITY FUNCTIONS =====
      
      resetMapState: () => {
        set({
          currentBounds: null,
          lastFetchedBounds: null,
          pendingBounds: null,
          mapStatus: initialMapStatus,
          isFlying: false,
          userHasInteracted: false,
          isMapLoading: false,
          mapError: null
        }, false, 'map/resetMapState');
      },

      isMapReady: () => {
        const { mapRef, mapStatus } = get();
        return mapRef && (mapStatus.state === 'ready' || mapStatus.state === 'idle');
      },
    }),
    {
      name: 'map-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// ===== SELECTOR HOOKS =====

/**
 * Optimized selectors for common use cases
 * These prevent unnecessary re-renders by only subscribing to specific data
 */

// Only re-render when map status changes
export const useMapStatus = () => 
  useMapStore(state => state.mapStatus);

// Only re-render when current bounds change
export const useCurrentBounds = () => 
  useMapStore(state => state.currentBounds);

// Only re-render when map loading state changes
export const useMapLoading = () => 
  useMapStore(state => state.isMapLoading);

// Only re-render when map ref changes
export const useMapRef = () => 
  useMapStore(state => state.mapRef);

// Only re-render when fullscreen state changes
export const useMapFullscreen = () => 
  useMapStore(state => state.isMapFullscreen);

// Combined selector for map readiness
export const useMapReadiness = () => 
  useMapStore(state => ({
    isReady: state.mapRef && (state.mapStatus.state === 'ready' || state.mapStatus.state === 'idle'),
    isLoading: state.isMapLoading,
    error: state.mapError,
    status: state.mapStatus.state
  }));

// ===== TYPE EXPORTS =====

export type { MapState, MapActions, MapStore }; 